import { useEffect, useMemo, useState } from 'react';
import {
  COLOR_LABEL,
  OUTCOME_LABEL,
  PIECE_LABEL,
  type ChessAction,
  type ChessView,
  type PlayerCard,
  type PromotionPiece,
} from '@bn/shared';
import { Alert, Badge, Button, Card } from '@/components/ui';
import { formatDuration, secondsLeft } from '@/lib/time';
import { ChessBoard } from './ChessBoard';
import {
  PIECE_GLYPH,
  findKing,
  materialBalance,
  parseFen,
  toMovePairs,
} from './helpers';

interface Props {
  view: ChessView;
  you: PlayerCard;
  opponent: PlayerCard;
  opponentOnline: boolean;
  clockOffset: number;
  onAction: (action: ChessAction) => Promise<void>;
}

export function ChessMatchView({
  view,
  you,
  opponent,
  opponentOnline,
  clockOffset,
  onAction,
}: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<{ from: string; to: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(() =>
    secondsLeft(view.deadlineAt, clockOffset),
  );

  useEffect(() => {
    setRemaining(secondsLeft(view.deadlineAt, clockOffset));
    if (view.deadlineAt === null || view.phase === 'finished') return;
    const timer = setInterval(
      () => setRemaining(secondsLeft(view.deadlineAt, clockOffset)),
      1000,
    );
    return () => clearInterval(timer);
  }, [view.deadlineAt, view.phase, clockOffset]);

  // Le coup de l'adversaire annule toute sélection en cours.
  useEffect(() => {
    setSelected(null);
    setPromotion(null);
  }, [view.fen]);

  const squares = useMemo(() => parseFen(view.fen), [view.fen]);
  const pairs = useMemo(() => toMovePairs(view.history), [view.history]);
  const balance = useMemo(() => materialBalance(view.captured), [view.captured]);
  const yourTurn = view.phase === 'playing' && view.turn === you.id;

  const targets = useMemo(() => {
    if (!selected) return new Set<string>();
    return new Set(
      view.legalMoves.filter((move) => move.from === selected).map((move) => move.to),
    );
  }, [selected, view.legalMoves]);

  const checkSquare = view.check ? findKing(squares, view.turnColor) : null;

  async function send(action: ChessAction) {
    setError(null);
    setBusy(true);
    try {
      await onAction(action);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Coup refusé.');
    } finally {
      setBusy(false);
    }
  }

  function handleSquare(square: string) {
    if (!yourTurn || busy) return;

    if (selected) {
      const candidates = view.legalMoves.filter(
        (move) => move.from === selected && move.to === square,
      );
      if (candidates.length > 0) {
        // Plusieurs coups pour la même case = promotion : il faut choisir la pièce.
        if (candidates.some((move) => move.promotion)) {
          setPromotion({ from: selected, to: square });
          return;
        }
        setSelected(null);
        void send({ type: 'move', from: selected, to: square });
        return;
      }
    }

    // Sélection d'une de ses propres pièces ayant au moins un coup légal.
    const playable = view.legalMoves.some((move) => move.from === square);
    setSelected(playable ? square : null);
  }

  const finished = view.phase === 'finished';
  const victory = finished && view.winner === you.id;
  const draw = finished && view.winner === null && view.endReason === 'draw';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700/60 bg-slate-900/60 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-100">
            {finished
              ? 'Partie terminée'
              : yourTurn
                ? 'À vous de jouer'
                : `Au tour de ${opponent.displayName}`}
          </span>
          {view.check && !finished && <Badge tone="alerte">Échec</Badge>}
          {remaining !== null && !finished && (
            <span
              className={`rounded-md px-2 py-0.5 font-mono text-sm ${
                remaining <= 15 ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-200'
              }`}
            >
              {formatDuration(remaining)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <span>
            {you.displayName}{' '}
            <span className="text-slate-500">({COLOR_LABEL[view.color]})</span>
          </span>
          <span className="text-slate-500">contre</span>
          <span className="flex items-center gap-1.5">
            {opponent.isBot ? 'Ordinateur' : opponent.displayName}
            <span className="text-slate-500">
              ({COLOR_LABEL[view.color === 'w' ? 'b' : 'w']})
            </span>
            <span
              title={opponentOnline ? 'Connecté' : 'Déconnecté'}
              className={`size-2 rounded-full ${
                opponentOnline ? 'bg-emerald-400' : 'bg-red-500'
              }`}
            />
          </span>
        </div>
      </div>

      {finished && (
        <Alert type={victory ? 'succes' : draw ? 'info' : 'erreur'}>
          {draw
            ? `Partie nulle — ${view.outcome ? OUTCOME_LABEL[view.outcome] : 'égalité'}.`
            : victory
              ? `Victoire — ${view.outcome ? OUTCOME_LABEL[view.outcome] : 'partie gagnée'}.`
              : `Défaite — ${view.outcome ? OUTCOME_LABEL[view.outcome] : 'partie perdue'}.`}
        </Alert>
      )}
      {error && <Alert>{error}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <Card>
          <CapturedRow
            pieces={view.captured[view.color === 'w' ? 'b' : 'w']}
            label={`Prises de ${you.displayName}`}
            advantage={view.color === 'w' ? balance : -balance}
          />

          <ChessBoard
            squares={squares}
            orientation={view.color}
            selected={selected}
            targets={targets}
            lastMove={view.lastMove}
            checkSquare={checkSquare}
            interactive={yourTurn && !busy}
            onSquareClick={handleSquare}
          />

          <CapturedRow
            pieces={view.captured[view.color]}
            label="Vos pièces perdues"
            advantage={null}
          />

          {promotion && (
            <div className="mt-4 rounded-lg border border-sky-500/40 bg-sky-950/40 p-3">
              <p className="mb-2 text-sm text-sky-100">Choisissez la pièce de promotion :</p>
              <div className="flex flex-wrap gap-2">
                {(['q', 'r', 'b', 'n'] as PromotionPiece[]).map((piece) => (
                  <Button
                    key={piece}
                    variant="secondaire"
                    onClick={() => {
                      const move = promotion;
                      setPromotion(null);
                      setSelected(null);
                      void send({ type: 'move', from: move.from, to: move.to, promotion: piece });
                    }}
                  >
                    <span className="text-xl">{PIECE_GLYPH[piece]}</span>
                    {PIECE_LABEL[piece]}
                  </Button>
                ))}
                <Button variant="discret" onClick={() => setPromotion(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {!finished && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm('Abandonner la partie ? La victoire ira à votre adversaire.')
                  ) {
                    void send({ type: 'resign' });
                  }
                }}
              >
                Abandonner
              </Button>
            </div>
          )}
        </Card>

        <Card title="Coups">
          <ol className="max-h-[28rem] space-y-0.5 overflow-y-auto pr-1 font-mono text-sm">
            {pairs.length === 0 && (
              <li className="font-sans text-slate-500">Aucun coup joué.</li>
            )}
            {pairs.map((pair) => (
              <li key={pair.number} className="flex gap-2 text-slate-300">
                <span className="w-6 shrink-0 text-right text-slate-500">{pair.number}.</span>
                <span className="w-16">{pair.white?.san ?? '…'}</span>
                <span className="w-16">{pair.black?.san ?? ''}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}

function CapturedRow({
  pieces,
  label,
  advantage,
}: {
  pieces: Array<keyof typeof PIECE_GLYPH>;
  label: string;
  advantage: number | null;
}) {
  return (
    <div className="mb-2 flex min-h-6 items-center gap-2 text-slate-400">
      <span className="sr-only">{label}</span>
      <span className="text-lg leading-none">
        {pieces.map((piece, index) => (
          <span key={`${piece}-${index}`}>{PIECE_GLYPH[piece]}</span>
        ))}
      </span>
      {advantage !== null && advantage > 0 && (
        <span className="text-xs font-semibold text-emerald-400">+{advantage}</span>
      )}
    </div>
  );
}
